/* eslint-disable react/jsx-props-no-spreading */
import { useTransition, animated, useSpringRef } from '@react-spring/web';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import { getComponentFromName } from '../../lib/utils';

import styles from '../../../styles/partials/slider.module.scss';

const propTypes = {
    component: PropTypes.func,
    componentName: PropTypes.string,
    components: PropTypes.objectOf(PropTypes.func),
    items: PropTypes.arrayOf(PropTypes.shape({})),
    itemProps: PropTypes.shape({}),
    index: PropTypes.number,
    duration: PropTypes.number,
    direction: PropTypes.oneOf(['top', 'bottom', 'left', 'right']), // Implement top, left if needed
    className: PropTypes.string,
};

const defaultProps = {
    component: null,
    componentName: null,
    components: null,
    items: [],
    itemProps: null,
    index: 0,
    duration: 8,
    direction: 'top',
    className: null,
};

function Slider({
    component,
    componentName,
    components,
    index: initialIndex,
    items,
    itemProps,
    duration,
    direction,
    className,
}) {
    const Component =
        getComponentFromName(components || {}, componentName, 'default') || component || null;

    const [index, setIndex] = useState(initialIndex);
    const count = useMemo(() => (items || []).length, [items]);

    const currentItem =
        useMemo(() => (items || []).find((_, i) => i === index), [items, index]) || null;
    const { duration: slideDuration = null } = currentItem || {};

    useEffect(() => {
        const id = setTimeout(() => {
            setIndex(index < count - 1 ? index + 1 : 0);
        }, (slideDuration || duration) * 1000);
        return () => {
            clearTimeout(id);
        };
    }, [index, count, duration, setIndex, slideDuration]);

    useEffect(() => {
        setIndex(initialIndex);
    }, [initialIndex]);

    const sliderRef = useSpringRef();
    const transitions = useTransition(index, {
        ref: sliderRef,
        keys: null,
        from: {
            opacity: 0,
            transform: direction === 'bottom' ? 'translate3d(0, 100%,0)' : 'translate3d(100%, 0,0)',
        },
        enter: { opacity: 1, transform: 'translate3d(0%,0%,0)' },
        leave: {
            opacity: 0,
            transform: direction === 'bottom' ? 'translate3d(0, 100%,0)' : 'translate3d(100%, 0,0)',
        },
    });

    useEffect(() => {
        sliderRef.start();
    }, [index]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            {transitions((style, i) => {
                const item = items[i] || {};
                return (
                    <animated.div className={styles.animated} style={{ ...style }}>
                        <Component {...itemProps} {...item} />
                    </animated.div>
                );
            })}
        </div>
    );
}

Slider.propTypes = propTypes;
Slider.defaultProps = defaultProps;

export default Slider;
